import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // ← Added
import Color from "../../components/Color";
import { useSnackbar } from "../../App";

const AddBrand = ({ open, onClose, formData, setFormData, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const nameInputRef = useRef(null);

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = ["Brand name is required"];
    } else if (!/^[a-zA-Z ]+$/.test(formData.name)) {
      newErrors.name = ["Brand name must contain only letters"];
    } else if (formData.name.length > 50) {
      newErrors.name = ["Brand name must not exceed 50 characters"];
    }

    if (formData.description && formData.description.length > 250) {
      newErrors.description = ["Description must not exceed 250 characters"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    setLoading(true);
    try {
      await onSubmit(setErrors);
    } catch (err) {
      if (err.response?.data?.errors?.name) {
        nameInputRef.current?.focus();
        setErrors((prev) => ({
          ...prev,
          name: err.response.data.errors.name,
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
    });
    setErrors({});
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "name") {
      const isValid = /^[a-zA-Z ]*$/.test(value);
      const filtered = value.replace(/[^a-zA-Z ]/g, "");
      const limitedValue = filtered.slice(0, 50);

      setFormData((prev) => ({
        ...prev,
        name: limitedValue,
      }));

      if (!limitedValue.trim()) {
        setErrors((prev) => ({
          ...prev,
          name: ["Brand name is required"],
        }));
      } else if (!isValid && value !== "") {
        setErrors((prev) => ({
          ...prev,
          name: ["Only letters and spaces are allowed"],
        }));
      } else if (limitedValue.length > 50) {
        setErrors((prev) => ({
          ...prev,
          name: ["Brand name must not exceed 50 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.name;
          return newErrors;
        });
      }
    } else if (name === "description") {
      const limitedValue = value.slice(0, 250);

      setFormData((prev) => ({
        ...prev,
        description: limitedValue,
      }));

      if (value.length > 250) {
        setErrors((prev) => ({
          ...prev,
          description: ["Description must not exceed 250 characters"],
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.description;
          return newErrors;
        });
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (loading) return;
        handleCancel();
      }}
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          width: isMobile ? "90vw" : "400px",
          maxWidth: "95vw",
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: isMobile ? "1rem" : "1.25rem",
          position: "relative",
          pr: 6, // Space for close button
        }}
      >
        {formData.id ? "Edit Brand" : "Add Brand"}

        {/* Close Icon Button */}
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1 : 2,
          maxHeight: isMobile ? "60vh" : "auto",
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 2,
            }}
          >
            {/* Brand Name */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Brand Name*
              </Typography>
              <TextField
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter brand name"
                fullWidth
                error={!!errors.name}
                helperText={
                  errors.name?.[0] || `${formData.name.length}/50`
                }
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputRef={nameInputRef}
                inputProps={{ maxLength: 50 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  },
                }}
              />
            </Box>

            {/* Description */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Description (Optional)
              </Typography>
              <TextField
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description"
                fullWidth
                error={!!errors.description}
                helperText={
                  errors.description?.[0] || `${formData.description.length}/250`
                }
                multiline
                rows={4}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 250 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-inputMultiline": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    lineHeight: 1.5,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  },
                }}
              />
            </Box>
          </Box>

          {errors.form && (
            <Typography color="error" variant="body2" mt={2}>
              {errors.form}
            </Typography>
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={1}>
            <Button
              onClick={handleCancel}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              sx={{
                background: Color.primary.main,
                color: "white",
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
                "&:hover": {
                  background: Color.primary.dark,
                },
                "&.Mui-disabled": {
                  background: Color.primary.main,
                  opacity: 0.7,
                  color: "white",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : formData.id ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddBrand;